/**
 * Pure logic for the Gems screen: stash grid, fuse costs/reasons, socket previews and the Gem
 * Power threshold tracks (120 / 240 / 360 / 480). DOM-free, unit-tested in gemsModel.test.ts.
 */
import type { ClassId } from '@keystone/sim';
import {
  type EquippedGear,
  type Gem,
  type GemColor,
  type GemPowerBonus,
  type GemStash,
  type Item,
  type Profile,
  GEMS_PER_FUSE,
  GEM_BONUSES,
  GEM_COLORS,
  GEM_THRESHOLD_LIST,
  ITEM_SLOTS,
  MAX_GEM_RANK,
  RANK_TIERS,
  fuse,
  fuseGoldCost,
  gemCount,
  gemPower,
  gemValue,
  hasUnlock,
  profileMaxGemRank,
  profileRating,
  socketMult,
  subRankBoundaries,
} from '@keystone/progression';
import { type ActionState, affordReason } from './upgradeModel';

// ---------------------------------------------------------------------------
// Unlocks
// ---------------------------------------------------------------------------

/** Where gems unlock ("Contender III" at N rating). */
export function gemsUnlockAt(): { label: string; rating: number } {
  for (const t of RANK_TIERS) {
    const u = t.unlocks.find((x) => x.unlock === 'gems');
    if (!u) continue;
    const sub = u.atSubRank ?? 1;
    const rating = sub <= 1 ? t.minRating : (subRankBoundaries(t.tier)[sub - 2] ?? t.minRating);
    return { label: `${t.name} ${['', 'I', 'II', 'III', 'IV', 'V'][sub] ?? sub}`, rating };
  }
  return { label: 'Contender III', rating: 500 };
}

export function gemsUnlocked(p: Profile): boolean {
  return hasUnlock(profileRating(p), 'gems');
}

/** Lowest tier whose gem rank cap allows `rank`. */
export function tierForGemRank(rank: number): string | null {
  return RANK_TIERS.find((t) => t.maxGemRank >= rank)?.name ?? null;
}

// ---------------------------------------------------------------------------
// Stash
// ---------------------------------------------------------------------------

export interface StashCell {
  gem: Gem;
  count: number;
  /** Gem Power of one gem in a normal socket. */
  power: number;
}

/** Every colour × rank (1..MAX_GEM_RANK), zero counts included, so the grid never jumps. */
export function stashGrid(stash: GemStash): Record<GemColor, StashCell[]> {
  const out = {} as Record<GemColor, StashCell[]>;
  for (const color of GEM_COLORS) {
    out[color] = Array.from({ length: MAX_GEM_RANK }, (_, i) => {
      const gem = { color, rank: i + 1 };
      return { gem, count: gemCount(stash, gem), power: gemValue(gem) };
    });
  }
  return out;
}

export function stashTotal(stash: GemStash): number {
  return Object.values(stash).reduce((a, b) => a + (b > 0 ? b : 0), 0);
}

// ---------------------------------------------------------------------------
// Fuse
// ---------------------------------------------------------------------------

export interface FusePreview {
  consumes: Gem;
  count: number;
  makes: Gem;
  /** Gem Power of the 3 inputs vs the 1 output (normal sockets). */
  powerIn: number;
  powerOut: number;
}

export function fuseAction(p: Profile, color: GemColor, rank: number): ActionState<FusePreview> {
  const cost = { gold: fuseGoldCost(rank) };
  const have = gemCount(p.gems, { color, rank });
  if (rank >= MAX_GEM_RANK) return { enabled: false, reason: `Rank ${MAX_GEM_RANK} is the highest rank.`, cost, preview: null };
  const makes = { color, rank: rank + 1 };
  const preview: FusePreview = { consumes: { color, rank }, count: GEMS_PER_FUSE, makes, powerIn: GEMS_PER_FUSE * gemValue({ color, rank }), powerOut: gemValue(makes) };
  if (!gemsUnlocked(p)) return { enabled: false, reason: `Gems unlock at ${gemsUnlockAt().label}.`, cost, preview };
  const max = profileMaxGemRank(p);
  const r = fuse(p.gems, color, rank, max);
  if (!r.ok) {
    const reason =
      r.reason === 'rankLocked'
        ? `Rank ${rank + 1} gems unlock at ${tierForGemRank(rank + 1) ?? 'a higher tier'} (your cap is rank ${max}).`
        : r.reason === 'notEnough'
          ? `Need ${GEMS_PER_FUSE} of this gem (you have ${have}).`
          : `Rank ${MAX_GEM_RANK} is the highest rank.`;
    return { enabled: false, reason, cost, preview };
  }
  const afford = affordReason(p, cost);
  return { enabled: afford === null, reason: afford, cost, preview };
}

// ---------------------------------------------------------------------------
// Sockets
// ---------------------------------------------------------------------------

export interface SocketPreview {
  color: GemColor;
  /** Change to that colour's Gem Power on the wearer. */
  delta: number;
  /** When replacing a gem of another colour, what that colour loses. */
  removed: { color: GemColor; delta: number } | null;
}

/** Equipped items with at least one socket, in slot order. */
export function socketedGear(gear: EquippedGear): Item[] {
  return ITEM_SLOTS.map((s) => gear[s]).filter((i): i is Item => !!i && i.sockets > 0);
}

export function socketAction(p: Profile, item: Item, socket: number, gem: Gem): ActionState<SocketPreview> {
  const cost = {};
  const mult = socketMult(item);
  const cur = item.gems[socket] ?? null;
  const add = gemValue(gem, mult);
  const sub = cur ? gemValue(cur, mult) : 0;
  const preview: SocketPreview =
    cur && cur.color !== gem.color ? { color: gem.color, delta: add, removed: { color: cur.color, delta: -sub } } : { color: gem.color, delta: add - sub, removed: null };
  if (!gemsUnlocked(p)) return { enabled: false, reason: `Gems unlock at ${gemsUnlockAt().label}.`, cost, preview };
  if (socket < 0 || socket >= item.sockets) return { enabled: false, reason: 'This item has no such socket.', cost, preview };
  if (gemCount(p.gems, gem) < 1) return { enabled: false, reason: 'You have none of that gem.', cost, preview };
  if (cur && cur.color === gem.color && cur.rank === gem.rank) return { enabled: false, reason: 'That gem is already in this socket.', cost, preview };
  return { enabled: true, reason: null, cost, preview };
}

// ---------------------------------------------------------------------------
// Gem Power thresholds
// ---------------------------------------------------------------------------

export interface ThresholdTrack {
  power: number;
  thresholds: readonly number[];
  /** Thresholds reached (0..4). */
  reached: number;
  /** Next threshold (null when all are reached). */
  next: number | null;
  /** Gem Power still needed for `next` (0 when maxed). */
  toNext: number;
  /** Fill of the whole track, 0..1 (power / last threshold). */
  fill: number;
  /** Fill of each segment [prev threshold, threshold], 0..1. */
  segments: number[];
}

/** Progress of one colour's Gem Power along the threshold track. */
export function thresholdTrack(power: number, thresholds: readonly number[] = GEM_THRESHOLD_LIST): ThresholdTrack {
  const p = Math.max(0, power);
  const reached = thresholds.filter((t) => p >= t).length;
  const next = thresholds[reached] ?? null;
  const last = thresholds[thresholds.length - 1] ?? 1;
  const segments = thresholds.map((t, i) => {
    const from = i === 0 ? 0 : thresholds[i - 1]!;
    return Math.max(0, Math.min(1, (p - from) / (t - from)));
  });
  return { power: p, thresholds, reached, next, toNext: next === null ? 0 : next - p, fill: Math.min(1, p / last), segments };
}

export interface ColorPowerView {
  color: GemColor;
  track: ThresholdTrack;
  /** This colour's bonus ladder for the class, each marked reached or not. */
  bonuses: { bonus: GemPowerBonus; reached: boolean }[];
  /** The next bonus to unlock (null when all reached). */
  nextBonus: GemPowerBonus | null;
}

export function gemPowerView(gear: EquippedGear, classId: ClassId, table: readonly GemPowerBonus[] = GEM_BONUSES): Record<GemColor, ColorPowerView> {
  const power = gemPower(gear);
  const out = {} as Record<GemColor, ColorPowerView>;
  for (const color of GEM_COLORS) {
    const ladder = table.filter((b) => b.color === color && (b.classId === null || b.classId === classId)).sort((a, b) => a.threshold - b.threshold);
    const thresholds = ladder.length ? ladder.map((b) => b.threshold) : GEM_THRESHOLD_LIST;
    const track = thresholdTrack(power[color], thresholds);
    const bonuses = ladder.map((bonus) => ({ bonus, reached: power[color] >= bonus.threshold }));
    out[color] = { color, track, bonuses, nextBonus: bonuses.find((b) => !b.reached)?.bonus ?? null };
  }
  return out;
}
